import {createClient} from '@sanity/client/stega'

const projectId =
  process.env.NEXT_PUBLIC_SANITY_PROJECT_ID || process.env.SANITY_PROJECT_ID || 'project-id'
const dataset = process.env.NEXT_PUBLIC_SANITY_DATASET || process.env.SANITY_DATASET || 'production'
const apiVersion =
  process.env.NEXT_PUBLIC_SANITY_API_VERSION || process.env.SANITY_API_VERSION || '2024-05-01'
const readToken =
  process.env.SANITY_READ_TOKEN || process.env.SANITY_API_READ_TOKEN || process.env.SANITY_API_TOKEN
const studioUrl = process.env.SANITY_STUDIO_URL || 'http://localhost:3333'

// Opt-in escape hatch for genuinely public datasets. Most of our datasets use
// fine-grained access control: anonymous reads return EMPTY (not an error) for
// gated types, which silently blanks the whole site (HTTP 200, no exception).
// Default is to fail loudly when a published read has no token; set this to '1'
// only when the dataset is verified readable anonymously.
const allowAnonymousPublishedReads =
  process.env.SANITY_ALLOW_ANONYMOUS_READS === '1' ||
  process.env.SANITY_ALLOW_ANONYMOUS_READS === 'true'

// Only enforce the read-token requirement in production builds/runtime. Dev and
// the test/storybook environment legitimately run tokenless against mocked or
// dummy data, so the guard there would be a false positive. `next build` for a
// deploy runs with NODE_ENV=production, so a missing token is still caught.
const enforceReadToken = process.env.NODE_ENV === 'production' && !allowAnonymousPublishedReads

// Keep an unauthenticated CDN client available for callers that explicitly need
// it, but default published reads use the read token with the CDN. Some deployed
// datasets are not readable anonymously, so CDN/no-token returns empty content.
export const sanityClient = createClient({
  projectId,
  dataset,
  apiVersion,
  useCdn: true,
})

export const getSanityClient = (draft: boolean, stega = draft) => {
  if (!draft) {
    if (!readToken) {
      // Token-gated datasets return empty content (not an error) to anonymous
      // clients. Returning the no-token client here is how the whole site
      // silently went blank in the 2026-05-26 incident. Fail fast instead.
      if (enforceReadToken) {
        throw new Error(
          'Missing SANITY_READ_TOKEN/SANITY_API_READ_TOKEN/SANITY_API_TOKEN for published reads. ' +
            'This dataset is likely token-gated and returns empty content anonymously. ' +
            'Provide a read token, or set SANITY_ALLOW_ANONYMOUS_READS=1 if the dataset is verified public.',
        )
      }
      return sanityClient.withConfig({
        stega: false,
      })
    }

    return sanityClient.withConfig({
      token: readToken,
      useCdn: true,
      perspective: 'published',
      stega: false,
    })
  }

  if (!readToken) {
    throw new Error('Missing SANITY_READ_TOKEN or SANITY_API_TOKEN for draft mode')
  }

  return sanityClient.withConfig({
    token: readToken,
    useCdn: false,
    perspective: 'drafts',
    stega: stega
      ? {
          enabled: true,
          studioUrl,
        }
      : false,
  })
}
